import _ from 'lodash';
import {
  FETCH_PROJECTS,
  FETCH_PROJECT,
  CREATE_PROJECT,
  REMOVE_PROJECT,
  LOGOUT,
} from './types';
import api from '../apis/api';
import { fetchModels } from './models';
import { fetchJobs } from './jobs';
import { fetchDatasets } from './datasets';
import getMessageFromApiError from '../util/getMessageFromApiError';
import { setErrorMessage } from './errorMessage';
import { fetchCheckpoints } from './checkpoints';
import { fetchVolumes } from './volumes';
import { fetchProjectGpuClasses } from './gpuClasses';

export const fetchProjects = () => async (dispatch) => {
  try {
    const res = await api.get('/project');
    dispatch({
      type: FETCH_PROJECTS,
      payload: res.data,
    });
  } catch (error) {
    if (error?.isAuthError) {
      return;
    }
    console.log(error);
    const message = getMessageFromApiError(error);

    // Handle authentication errors
    if (error.response?.status === 401) {
      console.log('Authentication failed, clearing session');
      dispatch({ type: LOGOUT });
      return;
    }

    if (message !== 'The incoming token has expired') {
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  }
};

export const fetchProject = (id) => async (dispatch) => {
  try {
    const res = await api.get(`/project/${id}`);
    if (_.isEmpty(res.data)) {
      dispatch({ type: REMOVE_PROJECT, payload: id });
    } else {
      dispatch({
        type: FETCH_PROJECT,
        payload: res.data,
      });
    }
  } catch (error) {
    if (error?.isAuthError) {
      return;
    }
    if (error.response && error.response.status === 404) {
      dispatch({ type: REMOVE_PROJECT, payload: id });
    } else {
      const message = getMessageFromApiError(error);

      // Handle authentication errors
      if (error.response?.status === 401) {
        console.log('Authentication failed, clearing session');
        dispatch({ type: LOGOUT });
        return;
      }

      if (message !== 'The incoming token has expired') {
        dispatch(setErrorMessage(message));
        throw new Error(`API Error - ${message}`);
      }
    }
  }
};

export const updateProject = (id, values) => async (dispatch) => {
  try {
    const res = await api.patch(`/project/${id} `, values);
    dispatch({
      type: FETCH_PROJECT,
      payload: res.data,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const refreshProjectGpuTypes = (id) => async (dispatch) => {
  try {
    await api.patch(`/project/${id}/gputypes`);
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const deleteProject = (id, user) => async (dispatch) => {
  try {
    if (user && id === user.selected_project) {
      dispatch(setSelectedProject(user.id, user));
    }
    await api.delete(`/project/${id}`);
    dispatch({
      type: REMOVE_PROJECT,
      payload: id,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const leaveProject = (id, user) => async (dispatch) => {
  try {
    if (user && id === user.selected_project) {
      dispatch(setSelectedProject(user.id, user));
    }
    await api.delete(`/project/${id}/access`, { params: { leave: true } });
    dispatch({
      type: REMOVE_PROJECT,
      payload: id,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const createProject = (values) => async (dispatch) => {
  try {
    const res = await api.post('/project', values);
    dispatch({
      type: CREATE_PROJECT,
      payload: res.data,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const setSelectedProject =
  (selected_project, currentUser) => async (dispatch) => {
    try {
      if (!currentUser || selected_project !== currentUser.selected_project) {
        try {
          const res = await api.patch('/customer', { selected_project });
          // Don't dispatch to Redux - context will handle this
          dispatch(fetchProjectGpuClasses(selected_project));
          dispatch(fetchJobs(selected_project));
          dispatch(fetchModels(selected_project));
          dispatch(fetchDatasets(selected_project));
          dispatch(fetchCheckpoints(selected_project));
          dispatch(fetchVolumes(selected_project));
          return res.data; // Return updated user for context update
        } catch (error) {
          console.log(error.res);
          throw error;
        }
      }
      return currentUser;
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };

export const getProject = async (id) => {
  try {
    const res = await api.get(`/project/${id}`);
    return res.data;
  } catch (error) {
    const message = getMessageFromApiError(error);
    console.log(message);
    throw new Error(`API Error - ${message}`);
  }
};
